import { useEffect, useState } from 'react';

import { DashboardApi } from 'Api/Dashboards/DashboardApi';
import { jsDateToIso8601 } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { DateRange } from 'Hooks/DateFilter';
import { ComplianceSummary, ControlStrengthResponse, ControlsSnapshot, MonthlyEnabledControlsSnapshot } from 'Models/Dashboards';

export const useControlStrengthQuery = (api: DashboardApi, userIsAuthorized: boolean): [ControlStrengthResponse | undefined, Error | undefined] => {
    const [controlStrength, setControlStrength] = useState<ControlStrengthResponse>();
    const [error, setError] = useState<Error>();

    useEffect(() => {
        const fetchControlStrength = async (): Promise<void> => {
            try {
                const controlStrengthResponse = await api.getControlStrength();
                setControlStrength(controlStrengthResponse.data);
            } catch (error) {
                setError(error);
            }
        };

        if (userIsAuthorized) {
            fetchControlStrength();
        }
    }, [api, userIsAuthorized]);

    return [controlStrength, error];
};

export const useCurrentComplianceSummaryQuery = (api: DashboardApi, userIsAuthorized: boolean): [ComplianceSummary[] | undefined, Error | undefined] => {
    const [complianceSummaries, setComplianceSummaries] = useState<ComplianceSummary[]>();
    const [error, setError] = useState<Error>();

    useEffect(() => {
        const fetchCurrentComplianceSummary = async (): Promise<void> => {
            try {
                const currentComplianceSummaries = await api.getCurrentComplianceSummary();
                setComplianceSummaries(currentComplianceSummaries.data.sort((complianceSummaryA, complianceSummaryB) => complianceSummaryA.regulation.localeCompare(complianceSummaryB.regulation)));
            } catch (error) {
                setError(error);
            }
        };

        if (userIsAuthorized) {
            fetchCurrentComplianceSummary();
        }
    }, [api, userIsAuthorized]);

    return [complianceSummaries, error];
};

export const useControlsHistoryQuery = (api: DashboardApi, dateRange: DateRange) => {
    const [controlsHistory, setControlsHistory] = useState<ControlsSnapshot[]>();
    const [controlHistoryError, setControlHistoryError] = useState<string>();

    useEffect(() => {
        const fetchControlHistory = async (): Promise<void> => {
            try {
                const controlHistory = await api.getControlsHistory(jsDateToIso8601(dateRange.startDate), jsDateToIso8601(dateRange.endDate));
                setControlsHistory(controlHistory.data);
            } catch (error) {
                setControlHistoryError(error.message);
            }
        };

        fetchControlHistory();
    }, [api, dateRange]);

    return { controlsHistory, controlHistoryError };
};

export const useMonthlyEnabledControlSnapshotQuery = (api: DashboardApi, dateRange: DateRange) => {
    const [monthlyEnabledControlSnapshots, setMonthlyEnabledControlSnapshots] = useState<MonthlyEnabledControlsSnapshot[]>();
    const [monthlyEnabledControlSnapshotsError, setMonthlyEnabledControlSnapshotsError] = useState<string>();
    const [maximumSnapshotControlCount, setMaximumSnapshotControlCount] = useState<number>();

    useEffect(() => {
        const fetchControlHistory = async (): Promise<void> => {
            try {
                const assessmentsResponse = await api.getMonthlyEnabledControlSnapshots(jsDateToIso8601(dateRange.startDate), jsDateToIso8601(dateRange.endDate));
                setMonthlyEnabledControlSnapshots(assessmentsResponse.data);
                if (assessmentsResponse.data) {
                    const maxCount = Math.max(...assessmentsResponse.data.map((snapshot) => snapshot.controls.length ?? 0));
                    setMaximumSnapshotControlCount(maxCount);
                }
            } catch (error) {
                setMonthlyEnabledControlSnapshotsError(error.message);
            }
        };

        fetchControlHistory();
    }, [api, dateRange]);

    return { monthlyEnabledControlSnapshots, monthlyEnabledControlSnapshotsError, maximumSnapshotControlCount };
};
